import {getTemplateDefinition} from "../data/template-catalog";
import {validateBannerCombination, type BannerSelection} from "../data/template-availability";
import {createGridDocument, validateGridDocument, patchBanner, type BannerInstance, type BannerPatch} from "./document";
import * as s from "./schema";
import {EXTRA_TEXT_FIELDS, TEMPLATE_APPEARANCE_FIELDS} from "./template-controls";
import {normalizeGdnColorVariants, normalizeGdnAccentColor} from "../libs/gdnColorCycle";
import {normalizeGdnTagOrder, INITIAL_GDN_TAGS} from "../libs/gdnTags";
import {sceneColorRecords, fieldColorsSchema, elementColorsSchema} from "../libs/sceneColors";
import {fromStoredEditableText, toStoredEditableText} from "../data/editable-text";

export type LegacyScene = Record<string, unknown>;
export const CONTENT_FIELDS = {
  title: "titles", subtitle: "subtitles", subtitle2: "subtitles2", subtitle3: "subtitles3",
  name: "names", position: "positions", date: "dates", badge: "badges", productName: "productNames",
  features: "features", eap: "eaps", link: "links", link2: "links2", link3: "links3", version: "versions",
} as const;
export const ALL_CONTENT_FIELDS = {...CONTENT_FIELDS, ...EXTRA_TEXT_FIELDS};
const TRANSFORM_FIELDS = {
  backgroundX: "bgX", backgroundY: "bgY", backgroundScale: "bgScale", backgroundRotate: "bgRotate", backgroundFlip: "bgFlip",
  uploadX: "uploadX", uploadY: "uploadY", uploadScale: "uploadScale", uploadRotate: "uploadRotate", uploadFlip: "uploadFlip",
  logoX: "logoX", logoY: "logoY", logoScale: "logoScale",
} as const;
function scoped(scene: LegacyScene, field: string, id: string, fallback: unknown, useMain = true): unknown {
  if (!Object.hasOwn(scene, field)) return fallback;
  const record = s.object(scene[field], `scene.${field}`);
  if (Object.hasOwn(record, id)) return record[id];
  if (useMain && Object.hasOwn(record, "main")) return record.main;
  return fallback;
}
function scalar(scene: LegacyScene, field: string, fallback: unknown, useMain = true): unknown {
  return Object.hasOwn(scene, field) ? scene[field] : fallback;
}
function legacyText(value: unknown, path: string): string {
  return fromStoredEditableText(s.text(value, path));
}
const EMPTY_FLIP = {x: false, y: false};

// Only the selected template is materialized. No main/template override maps remain in the document.
export function bannerFromLegacyScene(selection: BannerSelection & {instanceId: string; layout?: number}, scene: LegacyScene, patch: BannerPatch = {}): BannerInstance {
  s.object(scene, "scene");
  const resolved = validateBannerCombination(selection, selection.layout ?? Number(scalar(scene, "layoutOption", 1)));
  const id = resolved.definition.templateId;
  const fieldColors = sceneColorRecords(scene.fieldColors, fieldColorsSchema);
  const elementColors = sceneColorRecords(scene.elementColors, elementColorsSchema);
  const base = ["social", "email", "releases"].includes(resolved.definition.channel)
    ? scoped(scene, "bases", id, scalar(scene, "base", "000"), false) : scalar(scene, "base", "000");
  const content = Object.fromEntries(Object.entries(CONTENT_FIELDS).map(([field, legacy]) =>
    [field, legacyText(scoped(scene, legacy, id, field === "productName" ? ("shortLabel" in resolved.product ? resolved.product.shortLabel : resolved.product.label) : ""), `scene.${legacy}`)]));
  for (const [field, legacy] of Object.entries(EXTRA_TEXT_FIELDS)) {
    if (Object.hasOwn(scene, legacy)) content[field] = legacyText(scoped(scene, legacy, id, ""), `scene.${legacy}`);
  }
  const templateAppearance: Record<string, unknown> = {};
  for (const [field, legacy] of Object.entries(TEMPLATE_APPEARANCE_FIELDS)) {
    if (Object.hasOwn(scene, legacy)) templateAppearance[field] = field === "gdnColorVariants" ? normalizeGdnColorVariants(scene[legacy])
      : field === "gdnAccentColor" ? normalizeGdnAccentColor(scene[legacy]) : field === "variant" ? Number(scene[legacy]) : scene[legacy];
  }
  const tagRecord = Object.hasOwn(scene, "gdnTags") ? s.object(scene.gdnTags, "scene.gdnTags") : INITIAL_GDN_TAGS;
  const gdnContent = Object.hasOwn(scene, "gdnTags") || Object.hasOwn(scene, "gdnTagOrder") ? {
    gdnTags: Object.fromEntries(Object.entries(tagRecord).map(([key, value]) => [key, legacyText(value, `scene.gdnTags.${key}`)])),
    gdnTagOrder: normalizeGdnTagOrder(scene.gdnTagOrder, tagRecord),
  } : {};
  const transforms = Object.fromEntries(Object.entries(TRANSFORM_FIELDS).map(([field, legacy]) =>
    [field, scoped(scene, legacy, id, field.endsWith("Scale") ? 1 : field.endsWith("Flip") ? {...EMPTY_FLIP} : 0)]));
  const asset = (field: string, key = id, useMain = true) => {
    const value = scoped(scene, field, key, null, useMain);
    return value === "" ? null : value;
  };
  const slots: Record<string, unknown> = {};
  const slotKeys: Array<[string, string, string, number]> = resolved.definition.channel === "webinar"
    ? [["speaker-1", `${id}-speaker-1`, "speaker-1", 0], ["speaker-2", `${id}-speaker-2`, "speaker-2", 0]]
    : resolved.definition.channel === "events"
      ? [1, 2, 3].map((n): [string, string, string, number] => [`custom-qr-${n}`, `${id}-custom-qr-${n}`, `custom-qr-${n}`, 0])
    : resolved.definition.channel === "livestream"
      ? [1, 2, 3, 4, 5, 6].filter((n) => Object.hasOwn(s.object(scene.uploadImages ?? {}), `ls-speaker-${n}`) || ["uploadX", "uploadY", "uploadScale", "uploadRotate", "uploadFlip"].some((field) => Object.hasOwn(s.object(scene[field] ?? {}), `${id}-ls-speaker-${n}`))).map((n): [string, string, string, number] => [`ls-speaker-${n}`, `${id}-ls-speaker-${n}`, `ls-speaker-${n}`, 0])
    : id === "Screenshot" ? [["overlay", `overlay-${id}`, `overlay-${id}`, 100], ["cursor", `cursor-${id}`, `cursor-${id}`, 200]] : [];
  for (const [slot, key, imageKey, offset] of slotKeys) {
    slots[slot] = {url: asset("uploadImages", imageKey, false),
      x: scoped(scene, "uploadX", key, offset, false), y: scoped(scene, "uploadY", key, offset, false),
      scale: scoped(scene, "uploadScale", key, 1, false), rotate: scoped(scene, "uploadRotate", key, 0, false),
      flip: scoped(scene, "uploadFlip", key, {...EMPTY_FLIP}, false)};
  }
  const background = asset("customBgs") ?? (scalar(scene, "customBg", null) === "" ? null : scalar(scene, "customBg", null));
  const banner = {
    instanceId: selection.instanceId, templateKey: selection.templateKey, product: selection.product,
    contentType: selection.contentType, set: selection.set,
    content: {...content, ...gdnContent, tags: structuredClone(scalar(scene, "tags", {})),
      cursorLabel: legacyText(scoped(scene, "subtitles", `cursor-${id}`, "New Feature", false), "scene.cursorLabel")},
    appearance: {
      ...transforms, ...templateAppearance, layout: selection.layout ?? Number(scalar(scene, "layoutOption", 1)),
      base,
      backgroundOption: String(scalar(scene, "bgOption", "1")),
      font: scalar(scene, "fontOption", "en"), icon: scalar(scene, "iconOption", "key"),
      fontVariation: scalar(scene, "fontVariation", 1), uppercase: scalar(scene, "isUppercase", false),
      fontToggle: scalar(scene, "isFontToggle", false), logoHidden: scalar(scene, "logoHidden", false),
      textColor: scoped(scene, "textColorLocked", id, null),
      backgroundBrightness: scalar(scene, "bgBrightness", 100), backgroundBlur: scalar(scene, "bgBlurValue", 0),
      backgroundOpacity: scoped(scene, "bgOpacities", id, scalar(scene, "bgOpacity", 100)),
      buttonColor: scoped(scene, "buttonColors", id, null),
      ...(Object.hasOwn(fieldColors, id) ? {fieldColors: fieldColors[id]} : {}),
      ...(Object.hasOwn(elementColors, id) ? {elementColors: elementColors[id]} : {}),
      generative: structuredClone(scalar(scene, "generativeGradients", null)),
      generativeSpeed: scalar(scene, "generativeSpeed", 10000), generativeCount: scalar(scene, "generativeCount", 0),
      generativeStops: scalar(scene, "generativeStops", 3), regenerateKey: scalar(scene, "regenerateKey", 0),
    },
    assets: {background, image: asset("uploadImages"), logo: asset("logos"), logoVariant: asset("logoVariants"), slots},
    exportEnabled: !Array.isArray(scene.visible) || scene.visible.includes(id),
  };
  return structuredClone(patchBanner(banner, patch));
}

// Compatibility projection is used for rendering, never stored as a second source of state.
export function bannerToLegacyScene(banner: BannerInstance, targetId = getTemplateDefinition(banner.templateKey).templateId): LegacyScene {
  validateGridDocument({...createGridDocument(), schemaVersion: 2, banners: [banner]});
  const {definition, product, channel, type} = validateBannerCombination(banner, banner.appearance.layout);
  const a = banner.appearance;
  const scene: LegacyScene = {
    product: {...product, value: product.code}, channel: {value: channel.value, label: channel.label}, type,
    set: banner.set, layoutOption: a.layout, bgOption: a.backgroundOption, base: a.base,
    ...(a.fieldColors !== undefined ? {fieldColors: {[targetId]: structuredClone(a.fieldColors)}} : {}),
    ...(a.elementColors !== undefined ? {elementColors: {[targetId]: structuredClone(a.elementColors)}} : {}),
    fontOption: a.font, iconOption: a.icon, fontVariation: a.fontVariation,
    isUppercase: a.uppercase, isFontToggle: a.fontToggle, logoHidden: a.logoHidden,
    bgBrightness: a.backgroundBrightness, bgBlurValue: a.backgroundBlur,
    bgOpacity: a.backgroundOpacity ?? 100, buttonColors: a.buttonColor == null ? {} : {[targetId]: a.buttonColor},
    generativeGradients: structuredClone(a.generative), generativeSpeed: a.generativeSpeed,
    generativeCount: a.generativeCount, generativeStops: a.generativeStops, regenerateKey: a.regenerateKey,
    customBg: banner.assets.background ?? "", tags: {...banner.content.tags},
    visible: banner.exportEnabled ? [targetId] : [], order: [targetId], locked: [],
    uploadImages: {main: banner.assets.image, [targetId]: banner.assets.image},
    customBgs: {main: banner.assets.background, [targetId]: banner.assets.background},
    logos: {main: banner.assets.logo, [targetId]: banner.assets.logo},
    logoVariants: {[targetId]: banner.assets.logoVariant}, textColorLocked: {[targetId]: a.textColor},
  };
  for (const [field, legacy] of Object.entries(ALL_CONTENT_FIELDS)) {
    if (!Object.hasOwn(banner.content, field)) continue;
    const value = toStoredEditableText(banner.content[field]);
    scene[legacy] = {main: value, [targetId]: value};
  }
  for (const [field, legacy] of Object.entries(TEMPLATE_APPEARANCE_FIELDS)) {
    if (Object.hasOwn(a, field)) scene[legacy] = structuredClone(a[field]);
  }
  if (banner.content.gdnTags !== undefined) {
    scene.gdnTags = Object.fromEntries(Object.entries(banner.content.gdnTags).map(([key, value]) => [key, toStoredEditableText(value)]));
    scene.gdnTagOrder = [...banner.content.gdnTagOrder!];
  }
  for (const [field, legacy] of Object.entries(TRANSFORM_FIELDS)) scene[legacy] = {[targetId]: structuredClone(a[field])};
  for (const [slot, image] of Object.entries(banner.assets.slots)) {
    const key = (slot.startsWith("speaker-") || slot.startsWith("ls-speaker-") || slot.startsWith("custom-qr-")) ? `${targetId}-${slot}` : `${slot}-${targetId}`;
    const imageKey = (slot.startsWith("speaker-") || slot.startsWith("ls-speaker-") || slot.startsWith("custom-qr-")) ? slot : key;
    (scene.uploadImages as Record<string, unknown>)[imageKey] = image.url;
    for (const [name, value] of Object.entries(image)) {
      if (name === "url") continue;
      const field = `upload${name[0].toUpperCase()}${name.slice(1)}`;
      (scene[field] as Record<string, unknown>)[key] = structuredClone(value);
    }
  }
  if (definition.templateId === "Screenshot") (scene.subtitles as Record<string, string>)[`cursor-${targetId}`] = toStoredEditableText(banner.content.cursorLabel);
  return scene;
}
